import { useTheme } from '@mui/material/styles';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { chartColorsFor, chartMutedTextColor, chartSubtitleColor, chartTitleColor } from './chartColors';

interface IAgeRange {
  id: number;
  description: string;
  quantity: number;
}

const formatQuantity = (quantity: number) => quantity.toLocaleString('pt-BR');

const formatDocuments = (quantity: number) =>
  `${formatQuantity(quantity)} ${quantity === 1 ? 'documento' : 'documentos'}`;

const splitDescription = (description: string): string[] => {
  const words = description.split(' ');

  if (words.length <= 2) {
    return [description];
  }

  if (words[words.length - 1] === 'anos') {
    return [words.slice(0, -1).join(' '), 'anos'];
  }

  return [words.slice(0, 2).join(' '), words.slice(2).join(' ')];
};

export const AgeRangeTick = ({ x, y, payload, fill }: any) => (
  <g transform={`translate(${x},${y})`}>
    {splitDescription(String(payload.value)).map((line, index) => (
      <text key={line} x={0} y={0} dy={14 + index * 12} textAnchor="middle" fill={fill} fontSize={11} fontWeight={600}>
        {line}
      </text>
    ))}
  </g>
);

export const AgeRangeMedicalCertificateChart = ({ data }: { data: IAgeRange[] }) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';

  const barColors = chartColorsFor(isDark);
  const mutedText = chartMutedTextColor(isDark);

  const chartData = data.map((item, index) => ({
    ...item,
    quantity: Number(item.quantity) || 0,
    color: barColors[index % barColors.length],
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', width: '100%' }}>
      <p style={{ color: chartTitleColor, fontSize: 20, fontWeight: 700, margin: '0 0 4px 0', padding: '1px' }}>
        Quantidade de documentos por idade
      </p>
      <span style={{ color: chartSubtitleColor, fontSize: 13, fontWeight: 400 }}>
        Documentos por faixa etária dos colaboradores
      </span>

      <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 140 }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 24, right: 8, bottom: 16, left: -20 }}>
              <CartesianGrid vertical={false} stroke={palette.divider} strokeWidth={1} />

              <XAxis
                dataKey="description"
                interval={0}
                tickLine={false}
                axisLine={{ stroke: palette.divider }}
                tick={(tickProps: any) => <AgeRangeTick {...tickProps} fill={mutedText} />}
                height={44}
              />

              <YAxis
                type="number"
                domain={[0, 'auto']}
                allowDecimals={false}
                tickFormatter={value => formatQuantity(Number(value))}
                tick={{ fontSize: 12, fill: mutedText }}
                tickLine={false}
                axisLine={false}
              />

              <Tooltip
                cursor={{ fill: palette.action.hover }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const item = payload[0].payload as IAgeRange;
                  return (
                    <div
                      style={{
                        background: palette.background.paper,
                        border: `1px solid ${palette.divider}`,
                        borderRadius: 8,
                        padding: '10px 14px',
                        fontSize: 13,
                        color: palette.text.primary,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      }}
                    >
                      <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>{item.description}</p>
                      <p style={{ margin: '6px 0 0', color: mutedText }}>
                        <strong style={{ color: palette.text.primary }}>{formatDocuments(item.quantity)}</strong>
                      </p>
                    </div>
                  );
                }}
              />

              <Bar dataKey="quantity" name="Documentos" barSize={24} maxBarSize={24} radius={[4, 4, 0, 0]}>
                {chartData.map(item => (
                  <Cell key={item.id} fill={item.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
